import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Sun, Copy, Users, Moon, Inbox, BellRing, Check, Loader2, ArrowRight,
  MessageCircle, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { SITE } from "../../i18n/translations";

const STEPS = [
  { id: "read", label: "Read today's reflection once, slowly", hint: "So the message you send carries your own attention." },
  { id: "copy", label: "Copy the blessing message", hint: "One tap below — it is already formatted for WhatsApp." },
  { id: "send", label: "Send it to your broadcast list", hint: "WhatsApp → Settings → Broadcast lists → paste and send." },
  { id: "reply", label: "Reply to new enquiries", hint: "Answer the ones marked new before the day fills up." },
  { id: "remind", label: "Check reminders for the next sacred night", hint: "Message anyone waiting for the upcoming Amavasya." },
];

function StatTile({ icon: Icon, value, label, tone = "teal", testid }) {
  const tones = {
    teal: "from-tara-teal to-tara-tealLight",
    red: "from-tara-sindoor to-tara-vermilion",
    ink: "from-[#14262A] to-[#3E5257]",
  };
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <span className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${tones[tone]}`}>
        <Icon className="h-5 w-5 text-white" />
      </span>
      <p className="font-serif text-3xl font-bold text-tara-ink leading-none">{value}</p>
      <p className="text-[11px] uppercase tracking-[0.16em] text-tara-muted mt-2">{label}</p>
    </div>
  );
}

export default function TodayPanel({ onNavigate }) {
  const [darshan, setDarshan] = useState(null);
  const [subs, setSubs] = useState({ items: [], active: 0 });
  const [stats, setStats] = useState(null);
  const [newEnquiries, setNewEnquiries] = useState([]);
  const [festival, setFestival] = useState(null);
  const [reminders, setReminders] = useState(0);
  const [loading, setLoading] = useState(true);

  const dateKey = new Date().toISOString().slice(0, 10);
  const [checks, setChecks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`tt_routine_${dateKey}`)) || {}; } catch { return {}; }
  });

  const toggleStep = (id) => {
    const next = { ...checks, [id]: !checks[id] };
    setChecks(next);
    localStorage.setItem(`tt_routine_${dateKey}`, JSON.stringify(next));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, s, st, e, f, r] = await Promise.all([
        api.get("/darshan/today"),
        api.get("/admin/subscribers"),
        api.get("/admin/stats"),
        api.get("/admin/enquiries?status=new"),
        api.get("/festivals?limit=10"),
        api.get("/admin/reminders?pending=true"),
      ]);
      setDarshan(d.data || null);
      setSubs(s.data || { items: [], active: 0 });
      setStats(st.data);
      setNewEnquiries(e.data || []);
      setFestival((f.data || []).find((x) => x.major) || (f.data || [])[0] || null);
      setReminders(r.data?.upcoming || 0);
    } catch { toast.error("Could not load today's overview"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const message = darshan
    ? `🪔 Daily Darshan — ${SITE.name}\n\n“${darshan.text_en}”\n\nMaa Tara's blessings. — ${SITE.practitioner}`
    : "";
  const numbers = subs.items.filter((s) => s.active).map((s) => s.phone).join(", ");

  const daysToFestival = useMemo(() => {
    if (!festival) return null;
    const t = new Date(`${festival.date}T00:00:00`);
    const n = new Date(); n.setHours(0, 0, 0, 0);
    return Math.round((t - n) / 86400000);
  }, [festival]);

  const copy = async (value, label) => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); }
    catch { toast.error("Could not copy"); }
  };

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const doneCount = STEPS.filter((s) => checks[s.id]).length;

  if (loading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 text-tara-teal animate-spin" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="today-panel">
      {/* Hero row */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="glass-liquid relative overflow-hidden rounded-[1.8rem] p-8" data-testid="today-darshan-card">
          <span className="orb orb-gold h-56 w-56 -top-16 -right-10" />
          <div className="pattern-sacred absolute inset-0 opacity-20" />
          <div className="relative">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="badge-gold inline-flex items-center gap-2 rounded-full px-3.5 py-1 text-[10px] font-serif uppercase tracking-[0.24em]">
                <Sun className="h-3 w-3 flame-flicker" /> Today's darshan
              </span>
              <span className="text-[11px] uppercase tracking-[0.16em] text-tara-muted">
                {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
              </span>
            </div>
            <p className="font-serif text-xl text-tara-muted mb-1">{greeting}, {SITE.practitioner.split(" ")[0]}.</p>
            {darshan ? (
              <>
                <blockquote className="font-display text-2xl sm:text-[1.75rem] leading-[1.45] text-tara-ink italic my-5">
                  “{darshan.text_en}”
                </blockquote>
                <div className="flex flex-wrap gap-2.5">
                  <button
                    onClick={() => { copy(message, "Message"); if (!checks.copy) toggleStep("copy"); }}
                    className="btn-sacred flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold"
                    data-testid="today-copy-message"
                  >
                    <Copy className="h-4 w-4" /> Copy blessing
                  </button>
                  <button
                    onClick={() => copy(numbers, "Numbers")}
                    disabled={!numbers}
                    className="btn-ghost-gold flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold disabled:opacity-50"
                    data-testid="today-copy-numbers"
                  >
                    <Users className="h-4 w-4" /> Copy {subs.active} numbers
                  </button>
                </div>
              </>
            ) : (
              <p className="text-sm text-tara-muted my-6">
                No published reflection yet — add one in the Daily Darshan tab.
              </p>
            )}
          </div>
        </div>

        {/* Routine checklist */}
        <div className="glass-strong rounded-[1.8rem] p-7" data-testid="today-routine">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Morning routine
            </h3>
            <span className="text-xs font-semibold text-tara-muted" data-testid="today-routine-progress">{doneCount}/{STEPS.length}</span>
          </div>
          <div className="mb-5 h-1.5 rounded-full bg-tara-sand overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-tara-teal to-tara-vermilion transition-all duration-500"
              style={{ width: `${(doneCount / STEPS.length) * 100}%` }}
            />
          </div>
          <ul className="space-y-2.5">
            {STEPS.map((step) => {
              const done = !!checks[step.id];
              return (
                <li key={step.id}>
                  <button
                    onClick={() => toggleStep(step.id)}
                    className={`w-full text-left rounded-2xl border p-3.5 transition-all ${
                      done
                        ? "border-tara-teal/40 bg-tara-teal/[0.07]"
                        : "border-tara-gold/20 bg-white/70 hover:border-tara-gold/45"
                    }`}
                    data-testid={`today-step-${step.id}`}
                  >
                    <span className="flex items-start gap-3">
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                        done ? "border-transparent bg-gradient-to-br from-tara-teal to-tara-vermilion" : "border-tara-gold/40"
                      }`}>
                        {done && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span>
                        <span className={`block text-sm font-semibold ${done ? "text-tara-muted line-through" : "text-tara-ink"}`}>
                          {step.label}
                        </span>
                        <span className="block text-[11px] text-tara-muted mt-0.5">{step.hint}</span>
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Glance stats */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Inbox} value={stats?.new ?? 0} label="New enquiries" tone="red" testid="today-stat-new" />
        <StatTile icon={Users} value={subs.active} label="Darshan subscribers" tone="teal" testid="today-stat-subs" />
        <StatTile icon={BellRing} value={reminders} label="Pending reminders" tone="ink" testid="today-stat-reminders" />
        <StatTile
          icon={Moon}
          value={daysToFestival === null ? "—" : daysToFestival === 0 ? "Today" : `${daysToFestival}d`}
          label={festival ? festival.name : "Next sacred night"}
          tone="teal"
          testid="today-stat-festival"
        />
      </div>

      {/* New enquiries at a glance */}
      <div className="glass rounded-[1.8rem] p-7" data-testid="today-new-enquiries">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep">Waiting for your reply</h3>
          <button
            onClick={() => onNavigate?.("enquiries")}
            className="inline-flex items-center gap-1.5 text-sm font-serif font-semibold text-tara-goldDeep hover:gap-3 transition-all"
            data-testid="today-open-enquiries"
          >
            Open enquiries <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {newEnquiries.length === 0 ? (
          <p className="text-sm text-tara-muted">Nothing new — every enquiry has been picked up.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {newEnquiries.slice(0, 6).map((e) => (
              <div key={e.id} className="rounded-2xl border border-tara-gold/20 bg-white/70 p-4 flex items-start justify-between gap-3" data-testid={`today-enquiry-${e.id}`}>
                <div className="min-w-0">
                  <p className="font-display text-lg text-tara-ink leading-tight truncate">{e.name}</p>
                  <p className="text-[11px] text-tara-goldDeep mt-0.5 truncate">{e.service_title || "General enquiry"}</p>
                  {e.concern && <p className="text-xs text-tara-body mt-1.5 line-clamp-2">{e.concern}</p>}
                </div>
                {e.phone && (
                  <a
                    href={`https://wa.me/${e.phone.replace(/\D/g, "")}`}
                    target="_blank" rel="noopener noreferrer"
                    className="rounded-full border border-green-500/40 p-2 text-green-600 hover:bg-green-500/10 transition shrink-0"
                    aria-label="Reply on WhatsApp"
                    data-testid={`today-reply-${e.id}`}
                  >
                    <MessageCircle className="h-4 w-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
